import React, { createContext, useContext, useEffect, useState } from "react";
import { CustomFilter, ShipmentsPrimaryFilterType } from "../types";
import { EndCustomer, OrderAcquisitionType, RecurrenceType } from "@api/graphql/generated/generated-types";
import { useRouter } from "next/router";
import { useTableControl } from "./TableControlContext";
import { useLocalStorage } from "usehooks-ts";
import { useAuth } from "@src/common/hooks/useAuth";
import { getCourierTodayForDatePicker } from "@src/common/utils/timezoneConverters";
import { useRestoringViewContext } from "./RestoringViewContext";

// Define the context interface
interface ShipmentFiltersContextProps {
  dateFilter: Date;
  setDateFilter: (date: Date) => void;
  minDate: Date | undefined;
  setMinDate: (date: Date | undefined) => void;
  maxDate: Date | undefined;
  setMaxDate: (date: Date | undefined) => void;
  shipmentDateRangeStart: Date | undefined;
  shipmentDateRangeEnd: Date | undefined;
  setShipmentDateRange: (start: Date | undefined, end: Date | undefined) => void;
  completedDateRangeStart: Date | undefined;
  completedDateRangeEnd: Date | undefined;
  setCompletedDateRange: (start: Date | undefined, end: Date | undefined) => void;
  selectedPrimaryFilter: ShipmentsPrimaryFilterType;
  setSelectedPrimaryFilter: (filter: ShipmentsPrimaryFilterType) => void;
  selectedCustomFilter: CustomFilter | undefined;
  setSelectedCustomFilter: (filter: CustomFilter | undefined) => void;
  selectedDriverIdFilter: string | undefined;
  setSelectedDriverIdFilter: (driverId: string | undefined) => void;
  selectedCustomer: EndCustomer | undefined;
  setSelectedCustomer: (customer: EndCustomer | undefined) => void;
  trackingNumbersFilter: string;
  setTrackingNumbersFilter: (trackingNumbers: string) => void;
  displayIdsFilter: number | undefined;
  setDisplayIdsFilter: (displayIds: number | undefined) => void;
  orderNameSearch: string | undefined;
  setOrderNameSearch: (orderNameSearch: string | undefined) => void;
  searchTerm: string;
  setSearchTerm: (searchTerm: string) => void;
  hasOrderInternalNote: boolean;
  setHasOrderInternalNote: (hasOrderInternalNote: boolean) => void;
  hasDriverNoteOnly: boolean;
  setHasDriverNoteOnly: (hasDriverNoteOnly: boolean) => void;
  hasWaitTimeOrOverride: boolean;
  setHasWaitTimeOrOverride: (hasWaitTimeOrOverride: boolean) => void;
  hasMultipleDriverPayOnly: boolean;
  setHasMultipleDriverPayOnly: (hasMultipleDriverPayOnly: boolean) => void;
  hasUnapprovedDriverPackageReportOnly: boolean;
  setHasUnapprovedDriverPackageReportOnly: (
    hasUnapprovedDriverPackageReportOnly: boolean
  ) => void;
  isLate: boolean;
  setIsLate: (isLate: boolean) => void;
  isOnTime: boolean;
  setIsOnTime: (isOnTime: boolean) => void;
  selectedDriverLabelIds: string[];
  setSelectedDriverLabelIds: (labelIds: string[]) => void;
  /** Driver-label filter for the dispatch candidates sidebar (not the orders table). Saved per view. */
  dispatchDriverLabelIds: string[];
  setDispatchDriverLabelIds: (labelIds: string[]) => void;
  selectedOrderLabelIds: string[];
  setSelectedOrderLabelIds: (labelIds: string[]) => void;
  selectedEndCustomerLabelIds: string[];
  setSelectedEndCustomerLabelIds: (labelIds: string[]) => void;
  selectedCourierBrandIds: string[];
  setSelectedCourierBrandIds: (brandIds: string[]) => void;
  selectedAcquisitionTypes: OrderAcquisitionType[];
  setSelectedAcquisitionTypes: (types: OrderAcquisitionType[]) => void;
  selectedRecurrenceTypes: RecurrenceType[];
  setSelectedRecurrenceTypes: (types: RecurrenceType[]) => void;
  isAttempted: boolean;
  setIsAttempted: (isAttempted: boolean) => void;
  selectedPickupZoneIds: string[];
  setSelectedPickupZoneIds: (zoneIds: string[]) => void;
  selectedDropoffZoneIds: string[];
  setSelectedDropoffZoneIds: (zoneIds: string[]) => void;
  selectedPackageTypeIds: string[];
  setSelectedPackageTypeIds: (ids: string[]) => void;
  selectedOrderVehicleTypeIds: string[];
  setSelectedOrderVehicleTypeIds: (ids: string[]) => void;
  selectedDriverVehicleTypeIds: string[];
  setSelectedDriverVehicleTypeIds: (ids: string[]) => void;
  selectedServiceTypeIds: string[];
  setSelectedServiceTypeIds: (ids: string[]) => void;
  minWeightInPounds: number | undefined;
  setMinWeightInPounds: (weight: number | undefined) => void;
  maxWeightInPounds: number | undefined;
  setMaxWeightInPounds: (weight: number | undefined) => void;
  selectedRouteTemplateIds: string[];
  setSelectedRouteTemplateIds: (ids: string[]) => void;
}

// the context with a default value
const ShipmentFiltersContext =
  createContext<ShipmentFiltersContextProps | undefined>(undefined);

type Props = {
  children: React.ReactNode;
};

// Label ids are courier-specific, so the persisted sidebar filter is keyed per
// courier (CRI-166). Shared with the dispatch widget's no-context fallback.
export function dispatchDriverLabelFilterStorageKey(
  courierId: string | undefined | null
) {
  return `DispatchCandidateDriverLabelFilter:${courierId ?? ""}`;
}

// the context provider
const ShipmentFiltersProvider = ({ children }: Props) => {
  const router = useRouter();
  const courierToday = getCourierTodayForDatePicker();
  const { setFilters } = useTableControl();
  const { isRestoringView } = useRestoringViewContext();
  const [dateFilter, setDateFilter] = useState<Date>(courierToday);
  const [minDate, setMinDateRaw] = useState<Date | undefined>(courierToday);
  const [maxDate, setMaxDateRaw] = useState<Date | undefined>(courierToday);
  const [shipmentDateRangeStart, setShipmentDateRangeStartRaw] =
    useState<Date | undefined>(undefined);
  const [shipmentDateRangeEnd, setShipmentDateRangeEndRaw] =
    useState<Date | undefined>(undefined);
  const [completedDateRangeStart, setCompletedDateRangeStartRaw] =
    useState<Date | undefined>(undefined);
  const [completedDateRangeEnd, setCompletedDateRangeEndRaw] =
    useState<Date | undefined>(undefined);
  // Conditional state management for selectedPrimaryFilter
  const [localStorageFilter, setLocalStorageFilter] =
    useLocalStorage<ShipmentsPrimaryFilterType>(
      "selectedPrimaryFilter",
      ShipmentsPrimaryFilterType.ACTIVE
    );
  const [sessionFilter, setSessionFilter] =
    useState<ShipmentsPrimaryFilterType>(ShipmentsPrimaryFilterType.ACTIVE);

  const { courier } = useAuth();
  const courierId = courier?.courier?.id;
  const enableLocalStorageForPrimaryFilter = courierId === "COURKINGCOURIER";

  const selectedPrimaryFilter = enableLocalStorageForPrimaryFilter
    ? localStorageFilter
    : sessionFilter;
  const setSelectedPrimaryFilter = enableLocalStorageForPrimaryFilter
    ? setLocalStorageFilter
    : setSessionFilter;
  const [selectedCustomFilter, setSelectedCustomFilter] =
    useState<CustomFilter | undefined>(undefined);
  const [selectedDriverIdFilter, setSelectedDriverIdFilter] =
    useState<string | undefined>(undefined);
  const [selectedCustomer, setSelectedCustomer] =
    useState<EndCustomer | undefined>(undefined);
  const [trackingNumbersFilter, setTrackingNumbersFilter] =
    useState<string>("");
  const [displayIdsFilter, setDisplayIdsFilter] =
    useState<number | undefined>(undefined);
  const [orderNameSearch, setOrderNameSearch] =
    useState<string | undefined>(undefined);
  const [hasOrderInternalNote, setHasOrderInternalNote] =
    useState<boolean>(false);
  const [hasDriverNoteOnly, setHasDriverNoteOnly] = useState<boolean>(false);
  const [hasWaitTimeOrOverride, setHasWaitTimeOrOverride] =
    useState<boolean>(false);
  const [hasMultipleDriverPayOnly, setHasMultipleDriverPayOnly] =
    useState<boolean>(false);
  const [
    hasUnapprovedDriverPackageReportOnly,
    setHasUnapprovedDriverPackageReportOnly,
  ] = useState<boolean>(false);
  const [isLate, setIsLate] = useState<boolean>(false);
  const [isOnTime, setIsOnTime] = useState<boolean>(false);
  const [selectedDriverLabelIds, setSelectedDriverLabelIds] = useState<string[]>([]);
  const [dispatchDriverLabelIds, setDispatchDriverLabelIds] = useLocalStorage<
    string[]
  >(dispatchDriverLabelFilterStorageKey(courierId), []);
  const [selectedOrderLabelIds, setSelectedOrderLabelIds] = useState<string[]>([]);
  const [selectedEndCustomerLabelIds, setSelectedEndCustomerLabelIds] = useState<string[]>([]);
  const [selectedCourierBrandIds, setSelectedCourierBrandIds] = useState<string[]>([]);
  const [selectedAcquisitionTypes, setSelectedAcquisitionTypes] = useState<OrderAcquisitionType[]>([]);
  const [selectedRecurrenceTypes, setSelectedRecurrenceTypes] = useState<RecurrenceType[]>([]);
  const [isAttempted, setIsAttempted] = useState<boolean>(false);
  const [selectedPickupZoneIds, setSelectedPickupZoneIds] = useState<string[]>([]);
  const [selectedDropoffZoneIds, setSelectedDropoffZoneIds] = useState<string[]>([]);
  const [selectedPackageTypeIds, setSelectedPackageTypeIds] = useState<string[]>([]);
  const [selectedOrderVehicleTypeIds, setSelectedOrderVehicleTypeIds] = useState<string[]>([]);
  const [selectedDriverVehicleTypeIds, setSelectedDriverVehicleTypeIds] = useState<string[]>([]);
  const [selectedServiceTypeIds, setSelectedServiceTypeIds] = useState<string[]>([]);
  const [minWeightInPounds, setMinWeightInPounds] = useState<number | undefined>(undefined);
  const [maxWeightInPounds, setMaxWeightInPounds] = useState<number | undefined>(undefined);
  const [selectedRouteTemplateIds, setSelectedRouteTemplateIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = React.useState("");

  const clearNewRanges = () => {
    setShipmentDateRangeStartRaw(undefined);
    setShipmentDateRangeEndRaw(undefined);
    setCompletedDateRangeStartRaw(undefined);
    setCompletedDateRangeEndRaw(undefined);
  };

  const setMinDate = (date: Date | undefined) => {
    setMinDateRaw(date);
    if (date !== undefined) {
      clearNewRanges();
    }
  };

  const setMaxDate = (date: Date | undefined) => {
    setMaxDateRaw(date);
    if (date !== undefined) {
      clearNewRanges();
    }
  };

  const setShipmentDateRange = (
    start: Date | undefined,
    end: Date | undefined
  ) => {
    setShipmentDateRangeStartRaw(start);
    setShipmentDateRangeEndRaw(end);
    if (start !== undefined || end !== undefined) {
      setMinDateRaw(undefined);
      setMaxDateRaw(undefined);
    }
  };

  const setCompletedDateRange = (
    start: Date | undefined,
    end: Date | undefined
  ) => {
    setCompletedDateRangeStartRaw(start);
    setCompletedDateRangeEndRaw(end);
    if (start !== undefined || end !== undefined) {
      setMinDateRaw(undefined);
      setMaxDateRaw(undefined);
    }
  };

  useEffect(() => {
    if (
      trackingNumbersFilter.length > 3 ||
      displayIdsFilter !== undefined ||
      selectedCustomer !== undefined
    ) {
      setMinDateRaw(undefined);
      setMaxDateRaw(undefined);
      clearNewRanges();
    }
  }, [displayIdsFilter, trackingNumbersFilter]);

  // Effect for handling changes in searchTerm
  useEffect(() => {
    // Skip if restoring a saved view
    if (isRestoringView.current) return;

    if (searchTerm.length > 0) {
      setSelectedPrimaryFilter(ShipmentsPrimaryFilterType.ALL);
      setMinDateRaw(undefined);
      setMaxDateRaw(undefined);
      clearNewRanges();
      setTrackingNumbersFilter("");
      setDisplayIdsFilter(undefined);
      setOrderNameSearch(undefined);
    } else {
      setMinDateRaw(getCourierTodayForDatePicker());
      setMaxDateRaw(getCourierTodayForDatePicker());
    }
  }, [searchTerm]);

  useEffect(() => {
    // Skip if restoring a saved view
    if (isRestoringView.current) return;
    
    if (selectedPrimaryFilter !== ShipmentsPrimaryFilterType.ALL) {
      if (
        trackingNumbersFilter.length > 3 ||
        displayIdsFilter !== undefined ||
        selectedCustomer !== undefined
      ) {
        setSelectedPrimaryFilter(ShipmentsPrimaryFilterType.ALL);
      }
    }
  }, [
    trackingNumbersFilter,
    displayIdsFilter,
    selectedCustomer,
    selectedPrimaryFilter,
  ]);

  useEffect(() => {
    if (selectedPrimaryFilter !== ShipmentsPrimaryFilterType.ACTIVE) {
      setSelectedDriverIdFilter(undefined);
    }
  }, [selectedPrimaryFilter]);

  useEffect(() => {
    // Skip if restoring a saved view
    if (isRestoringView.current) return;
    
    if (
      selectedDriverIdFilter &&
      selectedPrimaryFilter === ShipmentsPrimaryFilterType.UNASSIGNED
    ) {
      setSelectedPrimaryFilter(ShipmentsPrimaryFilterType.ACTIVE);
    }
  }, [selectedDriverIdFilter]);

  // Handle URL parameters on mount and route changes
  useEffect(() => {
    const { filters } = router.query;
    const filtersObject =
      typeof filters === "string" ? JSON.parse(filters) : {};

    if (typeof filtersObject === "object" && filtersObject.trackingNumber) {
      setTrackingNumbersFilter(filtersObject.trackingNumber);
      setFilters((prevFilters) => [
        ...prevFilters.filter((f) => f.type !== "trackingNumbersFilter"),
        {
          id: 1,
          type: "trackingNumbersFilter",
          value: filtersObject.trackingNumber,
        },
      ]);

      // Remove the tracking parameter from URL after setting the filter
      router.replace("/deliveries/orders", undefined, { shallow: true });
    }
  }, [router.query]); // Listen to query changes

  return (
    <ShipmentFiltersContext.Provider
      value={{
        dateFilter,
        setDateFilter,
        minDate,
        setMinDate,
        maxDate,
        setMaxDate,
        shipmentDateRangeStart,
        shipmentDateRangeEnd,
        setShipmentDateRange,
        completedDateRangeStart,
        completedDateRangeEnd,
        setCompletedDateRange,
        selectedPrimaryFilter,
        setSelectedPrimaryFilter,
        selectedCustomFilter,
        setSelectedCustomFilter,
        selectedDriverIdFilter,
        setSelectedDriverIdFilter,
        selectedCustomer,
        setSelectedCustomer,
        trackingNumbersFilter,
        setTrackingNumbersFilter,
        displayIdsFilter,
        setDisplayIdsFilter,
        orderNameSearch,
        setOrderNameSearch,
        searchTerm,
        setSearchTerm,
        hasOrderInternalNote,
        setHasOrderInternalNote,
        hasDriverNoteOnly,
        setHasDriverNoteOnly,
        hasWaitTimeOrOverride,
        setHasWaitTimeOrOverride,
        hasMultipleDriverPayOnly,
        setHasMultipleDriverPayOnly,
        hasUnapprovedDriverPackageReportOnly,
        setHasUnapprovedDriverPackageReportOnly,
        isLate,
        setIsLate,
        isOnTime,
        setIsOnTime,
        selectedDriverLabelIds,
        setSelectedDriverLabelIds,
        dispatchDriverLabelIds,
        setDispatchDriverLabelIds,
        selectedOrderLabelIds,
        setSelectedOrderLabelIds,
        selectedEndCustomerLabelIds,
        setSelectedEndCustomerLabelIds,
        selectedCourierBrandIds,
        setSelectedCourierBrandIds,
        selectedAcquisitionTypes,
        setSelectedAcquisitionTypes,
        selectedRecurrenceTypes,
        setSelectedRecurrenceTypes,
        isAttempted,
        setIsAttempted,
        selectedPickupZoneIds,
        setSelectedPickupZoneIds,
        selectedDropoffZoneIds,
        setSelectedDropoffZoneIds,
        selectedPackageTypeIds,
        setSelectedPackageTypeIds,
        selectedOrderVehicleTypeIds,
        setSelectedOrderVehicleTypeIds,
        selectedDriverVehicleTypeIds,
        setSelectedDriverVehicleTypeIds,
        selectedServiceTypeIds,
        setSelectedServiceTypeIds,
        minWeightInPounds,
        setMinWeightInPounds,
        maxWeightInPounds,
        setMaxWeightInPounds,
        selectedRouteTemplateIds,
        setSelectedRouteTemplateIds,
      }}
    >
      {children}
    </ShipmentFiltersContext.Provider>
  );
};

// the custom hook
const useShipmentFiltersContext = () => {
  const context = useContext(ShipmentFiltersContext);
  if (context === undefined) {
    throw new Error(
      "useShipmentFiltersContext must be used within a ShipmentFiltersProvider"
    );
  }
  return context;
};

// Same as above but returns undefined when no provider is mounted
// (e.g. widgets that also render outside the orders page).
const useOptionalShipmentFiltersContext = () =>
  useContext(ShipmentFiltersContext);

export {
  ShipmentFiltersProvider,
  useShipmentFiltersContext,
  useOptionalShipmentFiltersContext,
};
